import { useState } from 'react'
import { Clock, Plus, User, Users, Info, Check, Trash2, Eye, ArrowRight, AlertTriangle, FileSpreadsheet, Upload, Download, Edit3, X, CheckCircle, AlertCircle } from 'lucide-react'
import Papa from 'papaparse'
import { useAuth } from '../contexts/AuthContext'
import { useSupabaseQuery, useSupabaseInsert, useSupabaseDelete, formatDate, formatTime, formatDuration } from '../hooks/useSupabase'
import { supabase } from '../lib/supabase'
import { Button, Card, Input, Select, Textarea, PageHeader, Toast, Badge, CheckboxGroup } from '../components/ui/SharedUI'

// ═══════════════════════════════════════════════════════════════
// CONSTANTS AND VALIDATION
// ═══════════════════════════════════════════════════════════════

const SHIFT_TYPES_LOGGED = [
  { value: 'direct_care', label: 'Direct care' },
  { value: 'no_show_covered', label: 'Covered no-show' },
  { value: 'training', label: 'Training' },
  { value: 'waiting_period', label: 'Waiting period' },
]

const SHIFT_TYPES_OBSERVED = [
  { value: 'direct_care', label: 'Direct care' },
  { value: 'training', label: 'Training' },
]

const VALID_SHIFT_TYPES = ['direct_care', 'no_show_covered', 'training', 'waiting_period']
const VALID_AUTHORSHIP = ['logged', 'observed']
const VALID_EVIDENCE_SOURCES = [
  'personal_observation', 'care_recipient_report', 'family_member_report',
  'pay_stub', 'agency_schedule', 'evv_record', 'text_message', 'other'
]
const VALID_TASKS = [
  'assist_self_admin_medication', 'assist_toileting', 'bathing_bed_sponge',
  'bathing_shower_tub', 'bed_mobility', 'bowel_bladder', 'catheter_hygiene',
  'clean_living_area', 'companionship', 'dress_undress', 'feeding_eating',
  'grooming', 'hygiene', 'laundry', 'laundry_home', 'make_change_bed_linen',
  'meal_preparation', 'mobility_transfer', 'ostomy_care', 'range_of_motion',
  'skin_care', 'transferring', 'transportation', 'vital_signs',
  'errands_shopping', 'exercise_assistance', 'respite_supervision', 'other',
]

const EVIDENCE_SOURCES = [
  { value: '', label: 'Select basis...' },
  { value: 'personal_observation', label: 'I was present and observed the shift' },
  { value: 'care_recipient_report', label: 'The care recipient told me about it' },
  { value: 'family_member_report', label: 'Another family member reported it' },
  { value: 'pay_stub', label: 'From a pay stub or timesheet' },
  { value: 'agency_schedule', label: 'From an agency schedule' },
  { value: 'evv_record', label: 'From an EVV screenshot' },
  { value: 'text_message', label: 'From a text message or email' },
  { value: 'other', label: 'Other (specify in notes)' },
]

const VALID_CONFIDENCE = ['documented', 'pattern', 'recollection']

// Honest strength tiers for backfilled entries. The whole point: a record
// that distinguishes bank-verified history from best-guess memory is MORE
// credible than one claiming flat certainty about everything.
const CONFIDENCE_LEVELS = [
  {
    value: 'documented',
    label: 'I have a record that backs this up',
    hint: 'Pay stub, bank deposit, text, EVV screenshot, calendar entry, etc.',
    short: 'Documented',
    color: '#005440',
    bg: '#E1F5EE',
  },
  {
    value: 'pattern',
    label: 'This follows my known regular schedule',
    hint: 'e.g. I always worked Tuesdays and Thursdays, 8am–4pm.',
    short: 'Pattern-based',
    color: '#2C7A7B',
    bg: '#EDF7F4',
  },
  {
    value: 'recollection',
    label: "I'm reconstructing this from memory",
    hint: 'No supporting record — your best honest recollection.',
    short: 'From memory',
    color: '#8b5000',
    bg: '#FCF5E8',
  },
]

const CONFIDENCE_MAP = Object.fromEntries(CONFIDENCE_LEVELS.map(c => [c.value, c]))

// Days-ago threshold beyond which a memory-only entry gets a gentle nudge
const OLD_ENTRY_DAYS = 60

function daysAgo(dateStr) {
  if (!dateStr) return 0
  const d = new Date(dateStr + 'T12:00:00')
  return Math.floor((Date.now() - d.getTime()) / 86400000)
}

const TASKS = [
  { value: 'assist_self_admin_medication', label: 'Medication assist' },
  { value: 'assist_toileting', label: 'Toileting' },
  { value: 'bathing_bed_sponge', label: 'Bed/sponge bath' },
  { value: 'bathing_shower_tub', label: 'Shower/tub' },
  { value: 'bed_mobility', label: 'Bed mobility' },
  { value: 'bowel_bladder', label: 'Bowel/bladder' },
  { value: 'catheter_hygiene', label: 'Catheter hygiene' },
  { value: 'clean_living_area', label: 'Clean living area' },
  { value: 'companionship', label: 'Companionship' },
  { value: 'dress_undress', label: 'Dress/undress' },
  { value: 'feeding_eating', label: 'Feeding' },
  { value: 'grooming', label: 'Grooming' },
  { value: 'hygiene', label: 'Hygiene' },
  { value: 'laundry', label: 'Laundry' },
  { value: 'meal_preparation', label: 'Meal prep' },
  { value: 'mobility_transfer', label: 'Mobility/transfer' },
  { value: 'range_of_motion', label: 'Range of motion' },
  { value: 'transferring', label: 'Transferring' },
  { value: 'vital_signs', label: 'Vital signs' },
  { value: 'other', label: 'Other' },
]

// ═══════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════

export function BackfillPage() {
  const { user, profile } = useAuth()
  const { data: recipients } = useSupabaseQuery('recipients', { column: 'owner_id', orderBy: 'name', ascending: true })
  const [mode, setMode] = useState('manual') // 'manual' or 'csv'

  return (
    <div style={{ padding: '2rem', maxWidth: '1000px' }}>
      <PageHeader
        title="Backfill shifts"
        description="Enter historical shifts you or someone else worked. Choose one-at-a-time entry for a few shifts, or CSV upload for many."
      />

      {/* Mode selector */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '1.5rem' }}>
        <label style={{
          display: 'flex', alignItems: 'flex-start', gap: '10px', padding: '14px',
          border: `1px solid ${mode === 'manual' ? '#2C7A7B' : '#E2E8F0'}`,
          borderRadius: '8px', cursor: 'pointer',
          background: mode === 'manual' ? '#E1F5EE' : '#FFFFFF',
        }}>
          <input type="radio" name="mode" value="manual" checked={mode === 'manual'}
            onChange={() => setMode('manual')} style={{ marginTop: '2px' }} />
          <div>
            <div style={{ fontSize: '14px', fontWeight: 500, color: '#1A202C', display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '2px' }}>
              <Edit3 size={15} /> Enter one at a time
            </div>
            <div style={{ fontSize: '13px', color: '#718096', lineHeight: 1.5 }}>
              Fill out a form for each shift. Best for a handful of entries.
            </div>
          </div>
        </label>

        <label style={{
          display: 'flex', alignItems: 'flex-start', gap: '10px', padding: '14px',
          border: `1px solid ${mode === 'csv' ? '#2C7A7B' : '#E2E8F0'}`,
          borderRadius: '8px', cursor: 'pointer',
          background: mode === 'csv' ? '#E1F5EE' : '#FFFFFF',
        }}>
          <input type="radio" name="mode" value="csv" checked={mode === 'csv'}
            onChange={() => setMode('csv')} style={{ marginTop: '2px' }} />
          <div>
            <div style={{ fontSize: '14px', fontWeight: 500, color: '#1A202C', display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '2px' }}>
              <FileSpreadsheet size={15} /> Upload a CSV file
            </div>
            <div style={{ fontSize: '13px', color: '#718096', lineHeight: 1.5 }}>
              Bulk import from a spreadsheet. Best for months of history.
            </div>
          </div>
        </label>
      </div>

      {mode === 'manual' ? <ManualBackfill recipients={recipients} /> : <CSVBackfill recipients={recipients} />}
    </div>
  )
}

// ═══════════════════════════════════════════════════════════════
// MANUAL ENTRY MODE
// ═══════════════════════════════════════════════════════════════

function ManualBackfill({ recipients }) {
  const { user } = useAuth()
  const { insert } = useSupabaseInsert('shifts')
  const { remove } = useSupabaseDelete('shifts')

  const [authorship, setAuthorship] = useState('logged')
  const [sessionShifts, setSessionShifts] = useState([])
  const [toast, setToast] = useState(null)
  const [saving, setSaving] = useState(false)

  const yesterday = new Date()
  yesterday.setDate(yesterday.getDate() - 1)

  const emptyForm = {
    date: yesterday.toISOString().split('T')[0],
    clock_in_time: '',
    clock_out_time: '',
    recipient_id: recipients.length === 1 ? recipients[0].id : '',
    shift_type: 'direct_care',
    confidence: '',
    observed_caregiver_name: '',
    evidence_source: '',
    evidence_source_note: '',
    tasks: [],
    notes: '',
    incident: false,
  }
  const [form, setForm] = useState(emptyForm)

  const recipientMap = Object.fromEntries(recipients.map(r => [r.id, r]))

  async function handleSubmit(e) {
    e.preventDefault()
    if (!form.date || !form.clock_in_time || !form.clock_out_time) return
    if (authorship === 'observed' && (!form.observed_caregiver_name || !form.evidence_source)) return

    setSaving(true)
    try {
      const clockIn = new Date(`${form.date}T${form.clock_in_time}:00`).toISOString()
      const clockOut = new Date(`${form.date}T${form.clock_out_time}:00`).toISOString()

      const payload = {
        caregiver_id: user.id,
        recipient_id: form.recipient_id || null,
        date: form.date,
        clock_in: clockIn,
        clock_out: clockOut,
        shift_type: form.shift_type,
        confidence: form.confidence || 'recollection',
        tasks: form.tasks,
        notes: form.notes || null,
        incident: form.incident,
        is_active: false,
        authorship,
        observed_caregiver_name: authorship === 'observed' ? form.observed_caregiver_name : null,
        evidence_source: authorship === 'observed' ? form.evidence_source : null,
        evidence_source_note: authorship === 'observed' ? (form.evidence_source_note || null) : null,
        is_backfilled: true,
        backfilled_at: new Date().toISOString(),
      }

      const saved = await insert(payload)
      setSessionShifts([saved, ...sessionShifts])

      setForm({
        ...emptyForm,
        date: form.date,
        recipient_id: form.recipient_id,
        confidence: form.confidence,
        observed_caregiver_name: authorship === 'observed' ? form.observed_caregiver_name : '',
        evidence_source: authorship === 'observed' ? form.evidence_source : '',
      })

      setToast({
        message: authorship === 'logged' ? 'Logged shift added' : 'Observed shift added',
        type: 'success',
      })
    } catch (err) {
      setToast({ message: err.message, type: 'error' })
    } finally {
      setSaving(false)
    }
  }

  async function handleRemove(id) {
    try {
      await remove(id)
      setSessionShifts(sessionShifts.filter(s => s.id !== id))
      setToast({ message: 'Entry removed', type: 'success' })
    } catch (err) {
      setToast({ message: err.message, type: 'error' })
    }
  }

  const isValid = form.date && form.clock_in_time && form.clock_out_time && form.confidence &&
    (authorship === 'logged' || (form.observed_caregiver_name && form.evidence_source))

  // Gentle nudge: an old, precise, memory-only entry with nothing backing it
  const showMemoryNudge =
    form.confidence === 'recollection' &&
    daysAgo(form.date) > OLD_ENTRY_DAYS &&
    !(authorship === 'observed' && form.evidence_source)

  return (
    <>
      {/* Explainer */}
      <Card style={{ marginBottom: '1.5rem', background: '#F7FAFC', borderLeft: '3px solid #2C7A7B' }}>
        <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-start' }}>
          <Info size={16} style={{ color: '#005440', marginTop: '2px', flexShrink: 0 }} />
          <div style={{ fontSize: '13px', color: '#4A5568', lineHeight: 1.7 }}>
            <strong style={{ color: '#1A202C' }}>About backfilled shifts.</strong> Entries are labeled as backfilled — they don't have the 30-minute lock of real-time clock-ins, and the timestamp of entry is recorded alongside the shift date. Observed shifts are additionally labeled with the source of information and never count toward your paid hours.
          </div>
        </div>
      </Card>

      {/* Authorship toggle */}
      <div style={{ marginBottom: '1.5rem' }}>
        <div style={{ fontSize: '13px', fontWeight: 500, color: '#4A5568', marginBottom: '8px' }}>
          What kind of shift are you entering?
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
          <label style={{
            display: 'flex', alignItems: 'flex-start', gap: '10px', padding: '14px',
            border: `1px solid ${authorship === 'logged' ? '#2C7A7B' : '#E2E8F0'}`,
            borderRadius: '8px', cursor: 'pointer',
            background: authorship === 'logged' ? '#E1F5EE' : '#FFFFFF',
          }}>
            <input type="radio" name="authorship" value="logged" checked={authorship === 'logged'}
              onChange={() => setAuthorship('logged')} style={{ marginTop: '2px' }} />
            <div>
              <div style={{ fontSize: '14px', fontWeight: 500, color: '#1A202C', display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '2px' }}>
                <User size={15} /> My own shift
              </div>
              <div style={{ fontSize: '13px', color: '#718096', lineHeight: 1.5 }}>
                A shift you worked yourself. Counts toward your hours and earnings.
              </div>
            </div>
          </label>

          <label style={{
            display: 'flex', alignItems: 'flex-start', gap: '10px', padding: '14px',
            border: `1px solid ${authorship === 'observed' ? '#8b5000' : '#E2E8F0'}`,
            borderRadius: '8px', cursor: 'pointer',
            background: authorship === 'observed' ? '#FCF5E8' : '#FFFFFF',
          }}>
            <input type="radio" name="authorship" value="observed" checked={authorship === 'observed'}
              onChange={() => setAuthorship('observed')} style={{ marginTop: '2px' }} />
            <div>
              <div style={{ fontSize: '14px', fontWeight: 500, color: '#1A202C', display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '2px' }}>
                <Eye size={15} /> Someone else's shift
              </div>
              <div style={{ fontSize: '13px', color: '#718096', lineHeight: 1.5 }}>
                A shift you witnessed or were told about. Does not count toward your earnings.
              </div>
            </div>
          </label>
        </div>
      </div>

      {/* Entry form */}
      <Card style={{ marginBottom: '1.5rem', borderLeft: authorship === 'observed' ? '3px solid #8b5000' : undefined }}>
        <form onSubmit={handleSubmit}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px' }}>
            <Input label="Date" id="bf-date" type="date" value={form.date}
              onChange={e => setForm({ ...form, date: e.target.value })}
              max={new Date().toISOString().split('T')[0]} required />
            <Input label="Clock in" id="bf-in" type="time" value={form.clock_in_time}
              onChange={e => setForm({ ...form, clock_in_time: e.target.value })} required />
            <Input label="Clock out" id="bf-out" type="time" value={form.clock_out_time}
              onChange={e => setForm({ ...form, clock_out_time: e.target.value })} required />
          </div>

          {recipients.length > 0 && (
            <Select label="Care recipient" id="bf-recipient" value={form.recipient_id}
              onChange={e => setForm({ ...form, recipient_id: e.target.value })}
              options={[
                { value: '', label: 'Select recipient...' },
                ...recipients.map(r => ({ value: r.id, label: r.name })),
              ]} />
          )}

          {authorship === 'observed' && (
            <>
              <Input label="Caregiver who worked this shift" id="bf-observed-name"
                value={form.observed_caregiver_name}
                onChange={e => setForm({ ...form, observed_caregiver_name: e.target.value })}
                required placeholder="e.g. Suzanne Miller, agency aide" />
              <Select label="How do you know about this shift?" id="bf-source"
                value={form.evidence_source}
                onChange={e => setForm({ ...form, evidence_source: e.target.value })}
                options={EVIDENCE_SOURCES} required />
              {form.evidence_source && (
                <Input label="Additional context about the source (optional)" id="bf-source-note"
                  value={form.evidence_source_note}
                  onChange={e => setForm({ ...form, evidence_source_note: e.target.value })}
                  placeholder="e.g. Mom mentioned Suzanne was there when I called at 10am" />
              )}
            </>
          )}

          <Select label="Shift type" id="bf-type" value={form.shift_type}
            onChange={e => setForm({ ...form, shift_type: e.target.value })}
            options={authorship === 'logged' ? SHIFT_TYPES_LOGGED : SHIFT_TYPES_OBSERVED} />

          {/* Confidence grading — honest strength of this reconstructed entry */}
          <div style={{ marginBottom: '1rem' }}>
            <div style={{ fontSize: '13px', fontWeight: 500, color: '#4A5568', marginBottom: '2px' }}>
              How sure are you about this entry?
            </div>
            <p style={{ fontSize: '12px', color: '#718096', marginTop: 0, marginBottom: '8px', lineHeight: 1.5 }}>
              Being honest about strength makes your record more credible, not less. A record that separates documented shifts from remembered ones holds up far better than one claiming perfect certainty about everything.
            </p>
            <div style={{ display: 'grid', gap: '8px' }}>
              {CONFIDENCE_LEVELS.map(level => {
                const selected = form.confidence === level.value
                return (
                  <label key={level.value} style={{
                    display: 'flex', alignItems: 'flex-start', gap: '10px', padding: '12px',
                    border: `1px solid ${selected ? level.color : '#E2E8F0'}`,
                    borderRadius: '8px', cursor: 'pointer',
                    background: selected ? level.bg : '#FFFFFF',
                  }}>
                    <input type="radio" name="confidence" value={level.value} checked={selected}
                      onChange={() => setForm({ ...form, confidence: level.value })} style={{ marginTop: '2px', flexShrink: 0 }} />
                    <div>
                      <div style={{ fontSize: '14px', fontWeight: 500, color: '#1A202C', marginBottom: '1px' }}>
                        {level.label}
                      </div>
                      <div style={{ fontSize: '12px', color: '#718096', lineHeight: 1.4 }}>
                        {level.hint}
                      </div>
                    </div>
                  </label>
                )
              })}
            </div>

            {showMemoryNudge && (
              <div style={{
                marginTop: '8px', padding: '10px 12px', background: '#FCF5E8',
                borderRadius: '8px', fontSize: '12px', color: '#8b5000', lineHeight: 1.6,
                display: 'flex', gap: '8px', alignItems: 'flex-start',
              }}>
                <Info size={13} style={{ marginTop: '2px', flexShrink: 0 }} />
                <span>
                  This is a memory-only entry from more than two months ago. Those carry less weight on their own. If you have anything that supports it — a pay stub, a text, a bank deposit, a calendar entry — noting it in the notes below makes this entry considerably stronger. You can still save it as-is.
                </span>
              </div>
            )}
          </div>

          <CheckboxGroup label="Tasks performed (optional)" options={TASKS} selected={form.tasks}
            onChange={tasks => setForm({ ...form, tasks })} />

          <Textarea label="Notes (optional)" id="bf-notes" value={form.notes}
            onChange={e => setForm({ ...form, notes: e.target.value })}
            placeholder={authorship === 'observed'
              ? "e.g. Mom said Suzanne seemed rushed and left early"
              : "What happened during this shift?"} />

          <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '1rem', fontSize: '14px', color: '#4A5568', cursor: 'pointer' }}>
            <input type="checkbox" checked={form.incident}
              onChange={e => setForm({ ...form, incident: e.target.checked })} />
            <AlertTriangle size={14} style={{ color: '#E07856' }} /> Flag as incident
          </label>

          <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
            <Button type="submit" disabled={!isValid || saving}>
              <Plus size={16} /> {saving ? 'Adding...' : 'Add and enter another'}
            </Button>
          </div>
        </form>
      </Card>

      {sessionShifts.length > 0 && (
        <div>
          <h2 style={{ fontSize: '14px', fontWeight: 500, color: '#718096', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '10px' }}>
            Added this session ({sessionShifts.length})
          </h2>
          <div style={{ display: 'grid', gap: '8px' }}>
            {sessionShifts.map(shift => (
              <Card key={shift.id} style={{
                padding: '10px 14px',
                borderLeft: shift.authorship === 'observed' ? '3px solid #8b5000' : '3px solid #2C7A7B',
                display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px',
              }}>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', marginBottom: '2px' }}>
                    <Badge variant={shift.authorship === 'observed' ? 'amber' : 'default'}>
                      {shift.authorship === 'observed' ? <><Eye size={11} style={{ verticalAlign: 'middle', marginRight: '2px' }} /> Observed</> : 'Logged'}
                    </Badge>
                    {shift.confidence && CONFIDENCE_MAP[shift.confidence] && (
                      <span style={{
                        fontSize: '11px', fontWeight: 500, padding: '2px 8px', borderRadius: '4px',
                        color: CONFIDENCE_MAP[shift.confidence].color,
                        background: CONFIDENCE_MAP[shift.confidence].bg,
                      }}>
                        {CONFIDENCE_MAP[shift.confidence].short}
                      </span>
                    )}
                    <span style={{ fontSize: '14px', fontWeight: 500, color: '#1A202C' }}>{formatDate(shift.date)}</span>
                    <span style={{ fontSize: '13px', color: '#718096' }}>
                      {formatTime(shift.clock_in)} – {formatTime(shift.clock_out)} · {formatDuration(shift.clock_in, shift.clock_out)}
                    </span>
                  </div>
                  <div style={{ fontSize: '12px', color: '#718096' }}>
                    {shift.recipient_id && `${recipientMap[shift.recipient_id]?.name || 'Recipient'} · `}
                    {shift.authorship === 'observed'
                      ? `Worked by ${shift.observed_caregiver_name}`
                      : shift.shift_type.replace(/_/g, ' ')
                    }
                  </div>
                </div>
                <Button variant="ghost" size="sm" onClick={() => handleRemove(shift.id)}>
                  <Trash2 size={14} />
                </Button>
              </Card>
            ))}
          </div>
        </div>
      )}

      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
    </>
  )
}

// ═══════════════════════════════════════════════════════════════
// CSV UPLOAD MODE
// ═══════════════════════════════════════════════════════════════

function CSVBackfill({ recipients }) {
  const { user } = useAuth()
  const [file, setFile] = useState(null)
  const [parsedRows, setParsedRows] = useState([])
  const [validationResults, setValidationResults] = useState([])
  const [importing, setImporting] = useState(false)
  const [importResults, setImportResults] = useState(null)
  const [toast, setToast] = useState(null)

  const recipientsByName = Object.fromEntries(
    recipients.map(r => [r.name.toLowerCase().trim(), r])
  )

  // ── Template download ──
  function downloadTemplate() {
    const template = [
      ['date', 'clock_in', 'clock_out', 'authorship', 'confidence', 'observed_caregiver_name', 'evidence_source', 'evidence_source_note', 'recipient_name', 'shift_type', 'tasks', 'notes', 'incident'],
      ['2026-03-15', '08:00', '16:00', 'logged', 'documented', '', '', '', recipients[0]?.name || 'Care recipient name', 'direct_care', 'bathing_bed_sponge; feeding_eating; grooming', 'Backed by pay stub', 'no'],
      ['2026-03-16', '08:00', '16:00', 'logged', 'pattern', '', '', '', recipients[0]?.name || 'Care recipient name', 'direct_care', '', 'My regular Monday schedule', 'no'],
      ['2026-03-17', '08:00', '16:00', 'observed', 'recollection', 'Suzanne Miller', 'care_recipient_report', 'Mom said Suzanne left early', recipients[0]?.name || 'Care recipient name', 'direct_care', '', 'Left an hour early per mom', 'yes'],
    ]

    const csv = template.map(row => row.map(cell => {
      const s = String(cell || '')
      if (s.includes(',') || s.includes('"') || s.includes('\n')) {
        return `"${s.replace(/"/g, '""')}"`
      }
      return s
    }).join(',')).join('\n')

    const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = 'kindredcare-backfill-template.csv'
    a.click()
    URL.revokeObjectURL(url)
  }

  // ── File handling and parsing ──
  function handleFileChange(e) {
    const uploaded = e.target.files?.[0]
    if (!uploaded) return
    setFile(uploaded)
    setImportResults(null)

    Papa.parse(uploaded, {
      header: true,
      skipEmptyLines: true,
      transformHeader: h => h.trim().toLowerCase().replace(/\s+/g, '_'),
      complete: (results) => {
        const rows = results.data
        setParsedRows(rows)
        setValidationResults(rows.map((row, i) => validateRow(row, i)))
      },
      error: (err) => {
        setToast({ message: `CSV parse error: ${err.message}`, type: 'error' })
      }
    })
  }

  function validateRow(row, index) {
    const errors = []
    const warnings = []

    // Date
    if (!row.date) {
      errors.push('Missing date')
    } else if (!/^\d{4}-\d{2}-\d{2}$/.test(row.date)) {
      errors.push('Date must be YYYY-MM-DD format')
    } else if (new Date(row.date + 'T12:00:00') > new Date()) {
      errors.push('Date is in the future')
    }

    // Times
    if (!row.clock_in) errors.push('Missing clock_in')
    else if (!/^\d{2}:\d{2}$/.test(row.clock_in)) errors.push('clock_in must be HH:MM (24-hour)')

    if (!row.clock_out) errors.push('Missing clock_out')
    else if (!/^\d{2}:\d{2}$/.test(row.clock_out)) errors.push('clock_out must be HH:MM (24-hour)')

    if (row.clock_in && row.clock_out && /^\d{2}:\d{2}$/.test(row.clock_in) && /^\d{2}:\d{2}$/.test(row.clock_out)) {
      const [inH, inM] = row.clock_in.split(':').map(Number)
      const [outH, outM] = row.clock_out.split(':').map(Number)
      if ((outH * 60 + outM) <= (inH * 60 + inM)) {
        errors.push('clock_out must be after clock_in')
      }
    }

    // Authorship
    const authorship = (row.authorship || '').toLowerCase().trim()
    if (!authorship) {
      errors.push('Missing authorship (must be "logged" or "observed")')
    } else if (!VALID_AUTHORSHIP.includes(authorship)) {
      errors.push(`Invalid authorship "${authorship}" — must be "logged" or "observed"`)
    }

    // Confidence (optional in CSV — defaults to recollection, the honest fallback)
    const confidence = (row.confidence || '').toLowerCase().trim()
    if (confidence && !VALID_CONFIDENCE.includes(confidence)) {
      errors.push(`Invalid confidence "${confidence}" — must be documented, pattern, or recollection`)
    } else if (!confidence) {
      warnings.push('No confidence set — will default to "from memory" (recollection)')
    }

    // Observed-specific
    if (authorship === 'observed') {
      if (!row.observed_caregiver_name?.trim()) {
        errors.push('observed_caregiver_name is required for observed shifts')
      }
      const source = (row.evidence_source || '').toLowerCase().trim()
      if (!source) {
        errors.push('evidence_source is required for observed shifts')
      } else if (!VALID_EVIDENCE_SOURCES.includes(source)) {
        errors.push(`Invalid evidence_source "${source}"`)
      }
    }

    // Recipient
    if (row.recipient_name?.trim()) {
      const match = recipientsByName[row.recipient_name.toLowerCase().trim()]
      if (!match) {
        warnings.push(`Recipient "${row.recipient_name}" not found — shift will be unassigned`)
      }
    }

    // Shift type
    const shiftType = (row.shift_type || 'direct_care').toLowerCase().trim()
    if (!VALID_SHIFT_TYPES.includes(shiftType)) {
      errors.push(`Invalid shift_type "${shiftType}"`)
    }

    // Tasks
    if (row.tasks?.trim()) {
      const taskList = row.tasks.split(';').map(t => t.trim().toLowerCase())
      const invalid = taskList.filter(t => t && !VALID_TASKS.includes(t))
      if (invalid.length > 0) {
        warnings.push(`Unknown tasks: ${invalid.join(', ')}`)
      }
    }

    return {
      index,
      row,
      errors,
      warnings,
      valid: errors.length === 0,
    }
  }

  // ── Import ──
  async function handleImport() {
    setImporting(true)
    const validRows = validationResults.filter(r => r.valid)
    let successCount = 0
    let errorCount = 0
    const errors = []

    for (const { row } of validRows) {
      try {
        const authorship = row.authorship.toLowerCase().trim()
        const recipientMatch = row.recipient_name?.trim()
          ? recipientsByName[row.recipient_name.toLowerCase().trim()]
          : null

        const taskList = row.tasks?.trim()
          ? row.tasks.split(';').map(t => t.trim().toLowerCase()).filter(t => t && VALID_TASKS.includes(t))
          : []

        const clockIn = new Date(`${row.date}T${row.clock_in}:00`).toISOString()
        const clockOut = new Date(`${row.date}T${row.clock_out}:00`).toISOString()

        const incident = ['yes', 'true', '1', 'y'].includes((row.incident || '').toLowerCase().trim())

        const payload = {
          caregiver_id: user.id,
          recipient_id: recipientMatch?.id || null,
          date: row.date,
          clock_in: clockIn,
          clock_out: clockOut,
          shift_type: (row.shift_type || 'direct_care').toLowerCase().trim(),
          confidence: VALID_CONFIDENCE.includes((row.confidence || '').toLowerCase().trim())
            ? (row.confidence || '').toLowerCase().trim()
            : 'recollection',
          tasks: taskList,
          notes: row.notes?.trim() || null,
          incident,
          is_active: false,
          authorship,
          observed_caregiver_name: authorship === 'observed' ? row.observed_caregiver_name.trim() : null,
          evidence_source: authorship === 'observed' ? row.evidence_source.toLowerCase().trim() : null,
          evidence_source_note: authorship === 'observed' ? (row.evidence_source_note?.trim() || null) : null,
          is_backfilled: true,
          backfilled_at: new Date().toISOString(),
        }

        const { error } = await supabase.from('shifts').insert(payload)
        if (error) {
          errorCount++
          errors.push(`Row ${validRows.indexOf({ row }) + 2}: ${error.message}`)
        } else {
          successCount++
        }
      } catch (err) {
        errorCount++
        errors.push(`Row error: ${err.message}`)
      }
    }

    setImportResults({ successCount, errorCount, errors, totalAttempted: validRows.length })
    setImporting(false)
  }

  function reset() {
    setFile(null)
    setParsedRows([])
    setValidationResults([])
    setImportResults(null)
  }

  const validCount = validationResults.filter(r => r.valid).length
  const invalidCount = validationResults.filter(r => !r.valid).length

  return (
    <>
      {/* Step 1: Instructions and template */}
      <Card style={{ marginBottom: '1.5rem' }}>
        <h3 style={{ fontSize: '15px', fontWeight: 500, color: '#1A202C', marginBottom: '10px' }}>
          Step 1 — Download the template
        </h3>
        <p style={{ fontSize: '14px', color: '#4A5568', marginBottom: '12px', lineHeight: 1.6 }}>
          Download the CSV template to see the required column format. Open it in Excel, Google Sheets, or Numbers, fill in your historical shifts, save as CSV, then upload below.
        </p>
        <Button variant="secondary" onClick={downloadTemplate}>
          <Download size={16} /> Download CSV template
        </Button>

        <details style={{ marginTop: '1rem' }}>
          <summary style={{ cursor: 'pointer', fontSize: '13px', fontWeight: 500, color: '#2C7A7B' }}>
            Show column reference
          </summary>
          <div style={{ marginTop: '10px', padding: '12px 14px', background: '#F7FAFC', borderRadius: '8px', fontSize: '13px', color: '#4A5568', lineHeight: 1.7 }}>
            <div><strong>date</strong> — YYYY-MM-DD (e.g. 2026-03-15). Required.</div>
            <div><strong>clock_in</strong> — HH:MM in 24-hour format (e.g. 08:00 or 16:30). Required.</div>
            <div><strong>clock_out</strong> — HH:MM in 24-hour format. Must be after clock_in. Required.</div>
            <div><strong>authorship</strong> — either <code>logged</code> (you worked it) or <code>observed</code> (someone else did). Required.</div>
            <div><strong>confidence</strong> — how sure you are: <code>documented</code> (backed by a pay stub, bank record, text, EVV, calendar), <code>pattern</code> (follows your regular schedule), or <code>recollection</code> (from memory). Defaults to <code>recollection</code> if blank.</div>
            <div><strong>observed_caregiver_name</strong> — Name of who worked it. Required only for observed shifts.</div>
            <div><strong>evidence_source</strong> — Required for observed. One of: <code>personal_observation</code>, <code>care_recipient_report</code>, <code>family_member_report</code>, <code>pay_stub</code>, <code>agency_schedule</code>, <code>evv_record</code>, <code>text_message</code>, <code>other</code>.</div>
            <div><strong>evidence_source_note</strong> — Optional context about the source.</div>
            <div><strong>recipient_name</strong> — Must match a care recipient you've added (e.g. "Carol Holloway"). Optional but recommended.</div>
            <div><strong>shift_type</strong> — One of: <code>direct_care</code>, <code>no_show_covered</code>, <code>training</code>, <code>waiting_period</code>. Defaults to direct_care.</div>
            <div><strong>tasks</strong> — Semicolon-separated list. e.g. <code>bathing_bed_sponge; feeding_eating; grooming</code></div>
            <div><strong>notes</strong> — Free-text description.</div>
            <div><strong>incident</strong> — <code>yes</code> or <code>no</code>. Defaults to no.</div>
          </div>
        </details>
      </Card>

      {/* Step 2: File upload */}
      {!parsedRows.length && !importResults && (
        <Card style={{ marginBottom: '1.5rem' }}>
          <h3 style={{ fontSize: '15px', fontWeight: 500, color: '#1A202C', marginBottom: '10px' }}>
            Step 2 — Upload your filled-in CSV
          </h3>
          <label style={{
            display: 'block', padding: '2rem', border: '2px dashed #E2E8F0',
            borderRadius: '10px', textAlign: 'center', cursor: 'pointer',
            transition: 'border-color 0.2s',
          }}>
            <input type="file" accept=".csv" onChange={handleFileChange} style={{ display: 'none' }} />
            <Upload size={32} style={{ color: '#A0AEC0', marginBottom: '10px' }} />
            <div style={{ fontSize: '15px', fontWeight: 500, color: '#1A202C', marginBottom: '4px' }}>
              Click to choose a CSV file
            </div>
            <div style={{ fontSize: '13px', color: '#718096' }}>
              Your file will be validated before anything is imported.
            </div>
          </label>
        </Card>
      )}

      {/* Step 3: Preview and validate */}
      {parsedRows.length > 0 && !importResults && (
        <>
          <Card style={{ marginBottom: '1rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
              <div>
                <h3 style={{ fontSize: '15px', fontWeight: 500, color: '#1A202C', marginBottom: '4px' }}>
                  Step 3 — Review before importing
                </h3>
                <div style={{ fontSize: '13px', color: '#718096' }}>
                  {file?.name} · {parsedRows.length} rows found
                </div>
              </div>
              <Button variant="ghost" size="sm" onClick={reset}>
                <X size={14} /> Choose different file
              </Button>
            </div>

            <div style={{ display: 'flex', gap: '12px', marginBottom: '12px' }}>
              <div style={{ padding: '10px 14px', background: '#E1F5EE', borderRadius: '8px', fontSize: '13px', color: '#005440', display: 'flex', alignItems: 'center', gap: '6px' }}>
                <CheckCircle size={14} /> <strong>{validCount}</strong> valid rows ready to import
              </div>
              {invalidCount > 0 && (
                <div style={{ padding: '10px 14px', background: '#FAECE7', borderRadius: '8px', fontSize: '13px', color: '#993C1D', display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <AlertCircle size={14} /> <strong>{invalidCount}</strong> rows have errors and will be skipped
                </div>
              )}
            </div>

            <Button onClick={handleImport} disabled={validCount === 0 || importing}>
              <Upload size={16} /> {importing ? 'Importing...' : `Import ${validCount} valid rows`}
            </Button>
          </Card>

          {/* Preview table */}
          <h4 style={{ fontSize: '13px', fontWeight: 500, color: '#718096', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '10px' }}>
            Preview
          </h4>
          <div style={{ display: 'grid', gap: '4px', maxHeight: '500px', overflow: 'auto' }}>
            {validationResults.map((result) => (
              <div key={result.index} style={{
                padding: '8px 12px',
                background: result.valid ? '#FFFFFF' : '#FAECE7',
                border: `1px solid ${result.valid ? '#E2E8F0' : '#E07856'}`,
                borderRadius: '6px',
                fontSize: '13px',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                  <span style={{ color: '#A0AEC0', fontFamily: 'monospace', fontSize: '11px' }}>#{result.index + 2}</span>
                  {result.valid ? <CheckCircle size={13} style={{ color: '#0F6E56' }} /> : <AlertCircle size={13} style={{ color: '#993C1D' }} />}
                  <span style={{ fontWeight: 500, color: '#1A202C' }}>{result.row.date}</span>
                  <span style={{ color: '#718096' }}>{result.row.clock_in}–{result.row.clock_out}</span>
                  <Badge variant={result.row.authorship === 'observed' ? 'amber' : 'default'}>
                    {result.row.authorship || 'unknown'}
                  </Badge>
                  {result.row.observed_caregiver_name && (
                    <span style={{ color: '#8b5000', fontSize: '12px' }}>· {result.row.observed_caregiver_name}</span>
                  )}
                  {result.row.recipient_name && (
                    <span style={{ color: '#718096', fontSize: '12px' }}>· {result.row.recipient_name}</span>
                  )}
                </div>
                {result.errors.length > 0 && (
                  <div style={{ marginTop: '4px', color: '#993C1D', fontSize: '12px' }}>
                    {result.errors.map((e, i) => <div key={i}>✗ {e}</div>)}
                  </div>
                )}
                {result.warnings.length > 0 && (
                  <div style={{ marginTop: '4px', color: '#8b5000', fontSize: '12px' }}>
                    {result.warnings.map((w, i) => <div key={i}>! {w}</div>)}
                  </div>
                )}
              </div>
            ))}
          </div>
        </>
      )}

      {/* Step 4: Results */}
      {importResults && (
        <Card>
          <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start', marginBottom: '1rem' }}>
            <div style={{ padding: '10px', background: '#E1F5EE', borderRadius: '10px' }}>
              <CheckCircle size={22} style={{ color: '#0F6E56' }} />
            </div>
            <div>
              <h3 style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: '20px', fontWeight: 500, color: '#1A202C', marginBottom: '4px' }}>
                Import complete
              </h3>
              <p style={{ fontSize: '14px', color: '#4A5568' }}>
                <strong style={{ color: '#0F6E56' }}>{importResults.successCount}</strong> shifts imported successfully
                {importResults.errorCount > 0 && <>, <strong style={{ color: '#993C1D' }}>{importResults.errorCount}</strong> failed</>}
              </p>
            </div>
          </div>

          {importResults.errors.length > 0 && (
            <div style={{ padding: '12px 14px', background: '#FAECE7', borderRadius: '8px', marginBottom: '1rem', fontSize: '13px', color: '#993C1D', lineHeight: 1.6 }}>
              <strong>Errors encountered:</strong>
              {importResults.errors.slice(0, 5).map((e, i) => <div key={i}>· {e}</div>)}
              {importResults.errors.length > 5 && <div>...and {importResults.errors.length - 5} more</div>}
            </div>
          )}

          <div style={{ display: 'flex', gap: '8px' }}>
            <Button variant="secondary" onClick={reset}>Upload another file</Button>
            <Button onClick={() => window.location.href = '/shifts'}>
              View shifts <ArrowRight size={14} />
            </Button>
          </div>
        </Card>
      )}

      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
    </>
  )
}
